/**
 * Eén rustige regel onder de fasenbalk: het Teams-projectkanaal (docs/teams-kanaal-automatisch.md).
 *
 * - gekoppeld/aangemaakt → link naar het kanaal;
 * - nog niet → wat Toolframe zou aanmaken + knop "Teamskanaal aanmaken" (in de proefstand: "Proefrun");
 * - geen klantteam → teamkeuze. ?teamskanaal=1 (de knop uit de mail) opent die keuze direct.
 *
 * Praat rechtstreeks met de engine (AI_ENGINE_URL + MSAL-token), zoals klant-afkortingen: identiek
 * op main en develop. Stand "off" = de regel verdwijnt.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, MenuItem, Select } from '@mui/material';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { teamsKanaalStatusLabel } from '../../utils/teamsKanaalStatus';
import { T } from '../ProjectOverview/parts/stitch';

const pad = (id) => `${AI_ENGINE_URL}/api/v1/teams/kanaal/project/${encodeURIComponent(id)}`;

export function regelTekst(data) {
  // Pure afleiding van wat de regel zegt — getest in __tests__/TeamsKanaalRegel.test.jsx.
  if (!data || data.modus === 'off' || !data.gevonden) return null;
  const rij = data.rij;
  const plan = data.plan;
  if (rij?.channel_id) {
    return { soort: 'klaar', tekst: rij.channel_name, team: rij.team_name, url: rij.web_url,
      extra: rij.status === 'gekoppeld' ? 'bestaand kanaal gekoppeld' : (rij.mappen_ok ? '' : 'mappen volgen') };
  }
  const proef = data.modus === 'dry';
  if (plan?.bestaand_kanaal) {
    return { soort: 'koppelen', proef, tekst: plan.bestaand_kanaal.channel_name, team: plan.bestaand_kanaal.team_name };
  }
  if (plan?.team) {
    return { soort: 'aanmaken', proef, tekst: plan.naam, team: plan.team.name, teamId: plan.team.id,
      gasten: plan.gasten !== false, reden: ['fout', 'geen_toegang'].includes(rij?.status) ? rij.fout : '' };
  }
  return { soort: 'kiezen', proef, tekst: plan?.naam || '', reden: plan?.team_bron || rij?.fout || data.plan_fout || '' };
}

const linkSx = {
  border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: T.fontBody,
  fontSize: '0.8rem', fontWeight: 600, color: T.tertiary, '&:hover': { textDecoration: 'underline' },
  '&:disabled': { color: T.outlineVariant, cursor: 'default', textDecoration: 'none' },
};

export default function TeamsKanaalRegel({ projectRecId }) {
  const [data, setData] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [melding, setMelding] = useState('');
  const [teamKeuze, setTeamKeuze] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [kiesOpen, setKiesOpen] = useState(false);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(pad(projectRecId));
      if (r.ok) setData(await r.json());
    } catch { /* regel blijft weg */ }
  }, [projectRecId]);

  useEffect(() => { if (projectRecId) laad(); }, [projectRecId, laad]);

  // ?teamskanaal=1 (de knop uit de mail) → teamkeuze open; parameter eraf zodat verversen hem niet heropent.
  useEffect(() => {
    if (searchParams.get('teamskanaal') !== '1') return;
    setKiesOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('teamskanaal');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const r = regelTekst(data);
  if (!r) return null;

  const teams = data?.plan?.teams || [];
  const gekozenTeam = teamKeuze || (r.soort === 'aanmaken' && !kiesOpen ? r.teamId : '');
  const teamNaam = teams.find((t) => t.id === gekozenTeam)?.name || r.team || '';

  const vraagGasten = () => bevestig({
    titel: 'Gasten in dit team',
    tekst: `In ${teamNaam || 'dit team'} zitten externe gasten, of dat is niet te controleren. Gasten kunnen een standaard kanaal en de bestanden erin zien. Toch aanmaken?`,
    knop: 'Toch aanmaken', gevaarlijk: true,
  });

  const verstuur = async (bevestigGasten) => {
    const res = await authFetch(pad(projectRecId), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ team_id: gekozenTeam || null, bevestig_gasten: bevestigGasten }),
    });
    const uit = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(uit.detail || uit.error || `HTTP ${res.status}`);
    return uit;
  };

  const start = async () => {
    setMelding('');
    let bevestigGasten = false;
    if (!r.proef) {
      const gevolg = r.soort === 'koppelen'
        ? `Het bestaande kanaal "${r.tekst}" wordt aan dit project gekoppeld. De PL krijgt een mail.`
        : `Er komt een standaard kanaal "${r.tekst}" in het team ${teamNaam}, met de vaste mappen. `
          + 'Raijmond, de PL en de ingeplande crew worden lid van dat team als ze dat nog niet zijn '
          + '(teamleden zien alle standaardkanalen van het team). De PL krijgt een mail.';
      if (!(await bevestig({ titel: 'Teams-kanaal', tekst: gevolg, knop: r.soort === 'koppelen' ? 'Koppelen' : 'Aanmaken' }))) return;
      if (r.gasten && !teamKeuze) {
        bevestigGasten = await vraagGasten();
        if (!bevestigGasten) return;
      }
    }
    setBezig(true);
    try {
      let uit = await verstuur(bevestigGasten);
      // Zelf gekozen team met (mogelijk) gasten: de server weigert eerst; na expliciete bevestiging opnieuw.
      if (!r.proef && !bevestigGasten && (uit.status === 'geen_toegang' || (uit.status === 'team_ontbreekt' && /gast/i.test(uit.reden || '')))) {
        setBezig(false);
        if (!(await vraagGasten())) { setMelding(uit.reden); await laad(); return; }
        setBezig(true);
        uit = await verstuur(true);
      }
      if (uit.modus === 'dry') {
        setMelding('Proefstand: er is niets aangemaakt. Het plan staat in Admin → Teams-kanaalsjabloon.');
      } else if (uit.status === 'aangemaakt' || uit.status === 'gekoppeld') {
        setMelding('');
      } else {
        setMelding(uit.reden || uit.rij?.fout || `Status: ${teamsKanaalStatusLabel(uit.status)}`);
      }
      setKiesOpen(false);
      await laad();
    } catch (e) {
      setMelding(e.message);
    } finally { setBezig(false); }
  };

  const tekstSx = { fontFamily: T.fontBody, fontSize: '0.8rem', color: T.secondary };
  return (
    <Box data-testid="teams-kanaal-regel" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1, ...tekstSx }}>
      <Box component="span" sx={{ color: T.outline }}>Teams-kanaal</Box>
      {r.soort === 'klaar' && (
        <>
          {r.url
            ? <Box component="a" href={r.url} target="_blank" rel="noreferrer" sx={{ ...linkSx, textDecoration: 'none' }}>{r.tekst} ↗</Box>
            : <Box component="span" sx={{ color: T.text }}>{r.tekst}</Box>}
          {r.team && <Box component="span">· {r.team}</Box>}
          {r.extra && <Box component="span" sx={{ color: T.outline }}>· {r.extra}</Box>}
        </>
      )}
      {r.soort !== 'klaar' && (
        <>
          {r.proef && <Box component="span" sx={{ color: T.outline }}>Proefstand ·</Box>}
          <Box component="span" sx={{ color: T.text }}>
            {r.soort === 'koppelen' && <>bestaat al: {r.tekst} ({r.team})</>}
            {r.soort === 'aanmaken' && !kiesOpen && <>nog niet aangemaakt — {r.tekst} in {r.team}</>}
            {(r.soort === 'kiezen' || kiesOpen) && <>{r.soort === 'kiezen' ? 'geen klantteam gevonden' : 'kies het team'}{r.reden && r.soort === 'kiezen' ? ` (${r.reden})` : ''}</>}
          </Box>
          {(r.soort === 'kiezen' || kiesOpen) && teams.length > 0 && (
            <Select size="small" value={teamKeuze} displayEmpty onChange={(e) => setTeamKeuze(e.target.value)}
              inputProps={{ 'aria-label': 'Team kiezen' }}
              sx={{ fontSize: '0.8rem', minWidth: 180, '& .MuiSelect-select': { py: 0.4 } }}>
              <MenuItem value="" disabled>Kies een team…</MenuItem>
              {teams.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          )}
          <Box component="button" type="button" onClick={start} sx={linkSx}
            disabled={bezig || ((r.soort === 'kiezen' || kiesOpen) && !teamKeuze)}>
            {bezig ? 'Bezig…' : r.proef ? 'Proefrun' : r.soort === 'koppelen' ? 'Kanaal koppelen'
              : (r.soort === 'kiezen' || kiesOpen) ? `Kanaal aanmaken in team ${teamNaam || '…'}` : 'Teamskanaal aanmaken'}
          </Box>
          {r.soort === 'aanmaken' && !kiesOpen && (
            <Box component="button" type="button" onClick={() => setKiesOpen(true)} sx={{ ...linkSx, color: T.outline, fontWeight: 500 }}>
              ander team
            </Box>
          )}
        </>
      )}
      {melding && <Box component="span" sx={{ width: '100%', color: T.outline }}>{melding}</Box>}
      {r.reden && r.soort === 'aanmaken' && <Box component="span" sx={{ width: '100%', color: T.error }}>{r.reden}</Box>}
    </Box>
  );
}
